import { useEffect, useRef, useState } from 'react';

/** The counter as the public stream sends it. */
export interface CounterLiveState {
  clinicId: string;
  clinicName: string;
  currentNumber: number;
  doctorIn: boolean;
  doctorName: string | null;
  updatedAt: string;
}

export type CounterSocketStatus = 'loading' | 'live' | 'reconnecting' | 'error';

const RECONNECT_DELAYS = [1000, 2000, 5000, 5000];

/**
 * Follow a clinic's counter live over its WebSocket stream.
 *
 * The stream is the public one the waiting-room display uses: it carries the
 * number being served and the doctor's status, and nothing about patients. Two
 * screens follow it — that display, and the doctor's "now seeing" page, which
 * needs to change patient the moment the front desk presses Next.
 *
 * `clinicRef` is a slug or an id; a missing one is reported as `error` rather
 * than attempted.
 */
export function useCounterSocket(clinicRef: string | null | undefined): {
  state: CounterLiveState | null;
  status: CounterSocketStatus;
} {
  const [state, setState] = useState<CounterLiveState | null>(null);
  const [status, setStatus] = useState<CounterSocketStatus>('loading');

  // Refs survive across reconnects so the close/reopen cycle doesn't restart
  // the effect (which would also tear down our reconnect timers).
  const wsRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const closedByUsRef = useRef(false);
  // Frames can arrive out of order if a broadcast races with the open-handler's
  // snapshot send. We keep the latest `updatedAt` and drop anything older.
  const lastUpdatedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!clinicRef) {
      setStatus('error');
      return;
    }

    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    closedByUsRef.current = false;

    const connect = () => {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(
        `${proto}//${window.location.host}/api/public/apt-counter/${encodeURIComponent(clinicRef)}/ws`,
      );
      wsRef.current = ws;
      ws.onopen = () => {
        attemptRef.current = 0;
        setStatus('live');
      };
      ws.onmessage = (evt) => {
        try {
          const next = JSON.parse(evt.data as string) as CounterLiveState;
          if (lastUpdatedRef.current !== null && next.updatedAt < lastUpdatedRef.current) {
            // Older snapshot arrived after a newer broadcast — ignore.
            return;
          }
          lastUpdatedRef.current = next.updatedAt;
          setState(next);
          setStatus('live');
        } catch {
          // Ignore non-JSON frames.
        }
      };
      ws.onclose = (evt) => {
        if (closedByUsRef.current) return;
        // Server-side rejection (e.g. unknown clinic) — don't loop.
        if (evt.code === 1008) {
          setStatus('error');
          return;
        }
        setStatus('reconnecting');
        const delay =
          RECONNECT_DELAYS[Math.min(attemptRef.current, RECONNECT_DELAYS.length - 1)]!;
        attemptRef.current += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        // The close handler will fire next; nothing to do here.
      };
    };

    connect();

    return () => {
      closedByUsRef.current = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      wsRef.current?.close();
    };
  }, [clinicRef]);

  return { state, status };
}
